import { Anchor, GlobalToken } from "antd";
import styled from "styled-components";
import { useAntTheme } from "hooks/useAntTheme";

export type ProfileNavItem = {
  /** Matches the id of the rendered section, without the leading `#`. */
  id: string;
  label: string;
};

type ProfileSideNavProps = Readonly<{
  items: ProfileNavItem[];
}>;

/**
 * Sticky left panel of the profile page. Clicking an item scrolls to its
 * section and scrolling the page highlights the item currently in view —
 * both handled by AntD's Anchor.
 */
export const ProfileSideNav = ({ items }: ProfileSideNavProps) => {
  const { theme } = useAntTheme();

  return (
    <StyledAnchor
      $token={theme}
      direction="vertical"
      affix
      offsetTop={24}
      targetOffset={24}
      // Anchor writes the section id to the URL; replace keeps the back button
      // pointing at the previous page instead of the previous anchor.
      replace
      items={items.map(({ id, label }) => ({
        key: id,
        href: `#${id}`,
        title: label,
      }))}
    />
  );
};

// ─── Styled components ───

// The class is doubled because AntD's own rules nest one level deeper than the
// generated styled-components class and would otherwise win.
const StyledAnchor = styled(Anchor)<{ $token: GlobalToken }>`
  /* AntD shows the active ink bar with a selector one class stronger than the
     doubled class used below, so this one is tripled. */
  &&& .ant-anchor .ant-anchor-ink.ant-anchor-ink-visible {
    display: none;
  }

  && .ant-anchor {
    background: transparent;

    /* Only the active item is marked, by its background: the rail spanning
       every item is hidden, and so is the ink bar (see below). */
    &::before {
      display: none;
    }

    .ant-anchor-link {
      padding: ${({ $token }) => `${$token.paddingXS}px ${$token.padding}px`};
    }

    .ant-anchor-link-active {
      background: ${({ $token }) => $token.controlItemBgActive};
    }

    .ant-anchor-link-active > .ant-anchor-link-title {
      color: ${({ $token }) => $token.colorPrimary};
      font-weight: ${({ $token }) => $token.fontWeightStrong};
    }
  }
`;
